import type { ApiClient, TokenResponse } from '@nyce/admin-kit/api';

/** Per-feature scopes. Any hub scope lets a user sign in; `ophub:*`, `ophub:pods:*` … wildcards match. */
export const SCOPES = {
  gcplogsRead: 'ophub:gcplogs:read',
  podsRead: 'ophub:pods:read',
  podsWrite: 'ophub:pods:write',
  dbRead: 'ophub:db:read',
  dbBackup: 'ophub:db:backup',
  dbCopy: 'ophub:db:copy',
  /** Index advisor: start scans and reviews, edit the default set. */
  dbAdvise: 'ophub:db:advise',
  admin: 'ophub:admin',
  authRead: 'ophub:auth:read',
  authWrite: 'ophub:auth:write',
  scimRead: 'ophub:scim:read',
  scimWrite: 'ophub:scim:write',
  statsRead: 'ophub:stats:read',
  statsAdmin: 'ophub:stats:admin',
  buddyRead: 'ophub:buddy:read',
  buddyAdmin: 'ophub:buddy:admin',
  designRead: 'ophub:design:read',
  designWrite: 'ophub:design:write',
  printqueueRead: 'ophub:printqueue:read',
  printqueueWrite: 'ophub:printqueue:write',
  automationRead: 'ophub:automation:read',
  automationWrite: 'ophub:automation:write',
  billingRead: 'ophub:billing:read',
  billingWrite: 'ophub:billing:write',
  all: 'ophub:*',
} as const;

/** Write scopes imply their read scope (the server already expands `/login` and `/me`; this covers token fallback). */
const IMPLIES: Record<string, string[]> = {
  [SCOPES.podsWrite]: [SCOPES.podsRead],
  [SCOPES.dbBackup]: [SCOPES.dbRead],
  [SCOPES.dbCopy]: [SCOPES.dbRead],
  [SCOPES.dbAdvise]: [SCOPES.dbRead],
  [SCOPES.authWrite]: [SCOPES.authRead],
  [SCOPES.scimWrite]: [SCOPES.scimRead],
  [SCOPES.statsAdmin]: [SCOPES.statsRead],
  [SCOPES.buddyAdmin]: [SCOPES.buddyRead],
  [SCOPES.designWrite]: [SCOPES.designRead],
  [SCOPES.printqueueWrite]: [SCOPES.printqueueRead],
  [SCOPES.automationWrite]: [SCOPES.automationRead],
  [SCOPES.billingWrite]: [SCOPES.billingRead],
};

/** `scopes` plus everything they imply, de-duplicated, order kept. */
export function expandScopes(scopes: readonly string[]): string[] {
  const out = new Set<string>();
  for (const s of scopes) {
    out.add(s);
    for (const i of IMPLIES[s] ?? []) out.add(i);
  }
  return [...out];
}

export interface LoginOptions {
  /** Label of the operator tenant, shown under the logo on the sign-in page. */
  operatorTenant: string;
  /** Federated sign-in (AUTH `/authorize`, e.g. Entra ID); null = password only. */
  sso?: { authUrl: string; clientId: string; tenant: string } | null;
  /** The environment this hub administers (`Hub:Environment`); null = every configured one. */
  environment?: { id: string; label: string } | null;
  /** The other environments' hubs: the menu's switch opens the same page there. */
  peers?: { label: string; url: string }[];
}

/** An MCP client's authorization request (OAuth 2.1, docs/mcp.md), as `/oauth/authorize` received it. */
export interface OAuthRequest {
  response_type: string;
  client_id: string;
  redirect_uri: string;
  code_challenge: string;
  code_challenge_method: string;
  state?: string;
  scope?: string;
  resource?: string;
}

export interface OAuthClientInfo {
  clientName: string | null;
  clientId: string;
  /** Where the browser goes back to (the client's host). */
  redirectHost: string;
}

/** The return of a federated sign-in, redeemed by the hub at AUTH. */
export interface LoginCodeRequest {
  code: string;
  codeVerifier: string;
  redirectUri: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse extends TokenResponse {
  refresh_token: string;
  user: { sub: string; name: string };
  scopes: string[];
  /** M8: a token pair (or error) per AUTH environment the caller may administer. */
  targets?: ({ id: string } & Partial<TokenResponse> & { error?: string; code?: string })[];
}

export interface Me {
  sub: string;
  name: string;
  tenantId: string;
  scopes: string[];
}

export type LinkEnvironment = 'production' | 'staging';

export interface LinkedTool {
  id: string;
  name: string;
  description: string;
  url: string;
  environment: LinkEnvironment;
  /** Pass-through sign-in: `POST /links/{id}/launch` returns the URL to open. */
  launch: boolean;
  /** lucide icon name (kebab or Pascal case), optional. */
  icon?: string;
}

// ---------- Registry (M2, docs/contracts.md) ----------

export type ClusterEnvironment = 'production' | 'staging';

export interface Cluster {
  id: string;
  name: string;
  projectId: string;
  location: string;
  clusterName: string;
  environment: ClusterEnvironment;
  enabled: boolean;
  updatedAt: string;
  /** `discovered` = inserted by GCP discovery. */
  source?: 'manual' | 'discovered';
}
export type ClusterInput = Omit<Cluster, 'id' | 'updatedAt' | 'source'>;

export type SslMode = 'disable' | 'prefer' | 'require';

export interface DbServer {
  id: string;
  name: string;
  host: string;
  port: number;
  username: string;
  hasPassword: boolean;
  sslMode: SslMode;
  environment: ClusterEnvironment;
  /** Never a copy/restore target. */
  protected: boolean;
  enabled: boolean;
  updatedAt: string;
  /** The discovered Cloud SQL / AlloyDB instance this server connects to (GCP resource name). */
  instance?: string | null;
}
/** `password` omitted (or empty) = keep the stored one. */
export type DbServerInput = Omit<DbServer, 'id' | 'updatedAt' | 'hasPassword'> & { password?: string };

export interface DbServerTest {
  ok: boolean;
  version?: string;
  databases?: number;
  latencyMs?: number;
  error?: string;
}

export type EnvironmentKind = 'production' | 'test' | 'staging' | 'dev';

export interface Environment {
  id: string;
  name: string;
  customer: string;
  kind: EnvironmentKind;
  clusterId: string | null;
  clusterName?: string;
  namespaces: string[];
  dbServerId: string | null;
  dbServerName?: string;
  dbName: string;
  baseUrl: string;
  notes: string;
  enabled: boolean;
  updatedAt: string;
  /** The installation's AUTH tenant (in the hub's own AUTH): links statistics (aliases `workserver:` / `pms:`) to it. */
  authTenantId?: string | null;
  /** Implicit environment for an unregistered namespace (id `<clusterId>:<namespace>`); from `/environments` only. */
  discovered?: boolean;
}
export type EnvironmentInput = Omit<
  Environment,
  'id' | 'updatedAt' | 'clusterName' | 'dbServerName' | 'discovered'
>;

/** A Cloud SQL or AlloyDB instance found in GCP (`/admin/discovery`). */
export interface DiscoveredInstance {
  kind: 'cloudsql' | 'alloydb';
  projectId: string;
  location: string;
  name: string;
  resourceName: string;
  cluster: string | null;
  role: 'primary' | 'replica' | 'read-pool';
  engine: string;
  version: string;
  state: string;
  health: 'ok' | 'warn' | 'down' | 'stopped';
  privateIp: string | null;
  publicIp: string | null;
  tier: string | null;
  /** The registered server linked to it. */
  serverId: string | null;
}

export interface Discovery {
  fetchedAt: string;
  projects: { id: string; name: string }[];
  clusters: {
    projectId: string;
    location: string;
    name: string;
    status: string;
    version: string | null;
    nodes: number | null;
    clusterId: string | null;
  }[];
  instances: DiscoveredInstance[];
  errors: { projectId: string; api: string; message: string }[];
}

export interface Link {
  id: string;
  name: string;
  description: string;
  icon: string;
  environment: LinkEnvironment;
  url: string;
  launchUrl: string | null;
  sort: number;
  enabled: boolean;
  updatedAt: string;
}
export type LinkInput = Omit<Link, 'id' | 'updatedAt'>;

export type AuditOutcome = 'ok' | 'error' | 'denied';

export interface AuditRecord {
  id: number;
  occurredAt: string;
  userSub: string;
  userName: string;
  action: string;
  targetType: string;
  targetId: string;
  outcome: AuditOutcome;
  detail: string;
  /** JSON text. */
  before: string | null;
  /** JSON text. */
  after: string | null;
}

export interface AuditQuery {
  limit?: number;
  /** Id cursor: only records older than this id. */
  before?: number | string;
  targetType?: string;
}

const seg = (id: string) => encodeURIComponent(id);

function query(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
}

/** Endpoint helpers over the generic client. */
export function hubApi(c: ApiClient) {
  return {
    loginOptions: () => c.get<LoginOptions>('/login/options', { auth: false }),
    login: (body: LoginRequest) => c.post<LoginResponse>('/login', body, { auth: false }),
    loginCode: (body: LoginCodeRequest) => c.post<LoginResponse>('/login/code', body, { auth: false }),
    /** MCP consent (docs/mcp.md): the client and redirect of an authorization request. */
    oauthCheck: (request: OAuthRequest) =>
      c.post<OAuthClientInfo>('/oauth/authorize/check', request, { auth: false }),
    /** Deny, or allow with the Entra sign-in the page made: where the browser goes back to the client. */
    oauthApprove: (body: OAuthRequest & { deny?: boolean; login?: LoginCodeRequest }) =>
      c.post<{ redirect: string }>('/oauth/authorize/approve', body, { auth: false }),
    logout: (refreshToken?: string) =>
      c.post<void>('/logout', { refresh_token: refreshToken ?? '' }, { auth: false }),
    me: () => c.get<Me>('/me'),
    /** AUTH user ids → operator names from the audit log (any hub scope); unknown ids are absent. */
    operatorNames: (ids: string[]) =>
      c.get<Record<string, string>>(
        `/operators/names?${ids.map((id) => `sub=${encodeURIComponent(id)}`).join('&')}`
      ),
    links: () => c.get<LinkedTool[]>('/links'),
    launchLink: (id: string) => c.post<{ url: string }>(`/links/${encodeURIComponent(id)}/launch`),
    /** Enabled environments, for pickers (any hub scope). */
    environments: () => c.get<Environment[]>('/environments'),
    admin: {
      clusters: {
        list: () => c.get<Cluster[]>('/admin/clusters'),
        put: (id: string, body: ClusterInput) => c.put<Cluster>(`/admin/clusters/${seg(id)}`, body),
        remove: (id: string) => c.del<void>(`/admin/clusters/${seg(id)}`),
      },
      dbServers: {
        list: () => c.get<DbServer[]>('/admin/db-servers'),
        put: (id: string, body: DbServerInput) => c.put<DbServer>(`/admin/db-servers/${seg(id)}`, body),
        remove: (id: string) => c.del<void>(`/admin/db-servers/${seg(id)}`),
        test: (id: string) => c.post<DbServerTest>(`/admin/db-servers/${seg(id)}/test`),
        databases: (id: string) => c.get<string[]>(`/admin/db-servers/${seg(id)}/databases`),
      },
      environments: {
        list: () => c.get<Environment[]>('/admin/environments'),
        put: (id: string, body: EnvironmentInput) =>
          c.put<Environment>(`/admin/environments/${seg(id)}`, body),
        remove: (id: string) => c.del<void>(`/admin/environments/${seg(id)}`),
      },
      links: {
        list: () => c.get<Link[]>('/admin/links'),
        put: (id: string, body: LinkInput) => c.put<Link>(`/admin/links/${seg(id)}`, body),
        remove: (id: string) => c.del<void>(`/admin/links/${seg(id)}`),
      },
      discovery: (refresh = false) => c.get<Discovery>(`/admin/discovery${refresh ? '?refresh=true' : ''}`),
      audit: (q: AuditQuery = {}) =>
        c.get<AuditRecord[]>(
          `/admin/audit${query({ limit: q.limit, before: q.before, targetType: q.targetType })}`
        ),
    },
  };
}

export type HubApi = ReturnType<typeof hubApi>;
